import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Search, Tractor } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import AuthImage from "@/components/AuthImage";

const PLACEHOLDERS = {
  Work: "https://images.unsplash.com/photo-1589746164246-8f32d747c43c?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
  Home: "https://images.pexels.com/photos/7019609/pexels-photo-7019609.jpeg?auto=compress&cs=tinysrgb&w=1200",
};

export default function Equipment() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const params = {};
        if (category !== "all") params.category = category;
        if (search) params.search = search;
        const { data } = await api.get("/equipment", { params });
        setItems(data.items);
      } finally {
        setLoading(false);
      }
    })();
  }, [category, search]);

  return (
    <div className="p-6 sm:p-8 lg:p-10 max-w-[1600px] mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-orange-500 mb-2">// Asset registry</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter">Garage</h1>
          <p className="text-zinc-400 mt-2">{items.length} piece{items.length === 1 ? "" : "s"} of equipment in the logbook.</p>
        </div>
        <Link to="/app/equipment/new">
          <Button className="bg-orange-600 hover:bg-orange-500 text-white rounded-sm uppercase tracking-widest font-bold text-xs" data-testid="new-equipment-btn">
            <Plus className="w-4 h-4 mr-1" /> Add equipment
          </Button>
        </Link>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <Tabs value={category} onValueChange={setCategory}>
          <TabsList className="bg-zinc-900 border border-zinc-800 rounded-sm p-1">
            <TabsTrigger value="all" className="rounded-sm data-[state=active]:bg-orange-600 uppercase tracking-widest text-xs" data-testid="filter-all">All</TabsTrigger>
            <TabsTrigger value="Home" className="rounded-sm data-[state=active]:bg-orange-600 uppercase tracking-widest text-xs" data-testid="filter-home">Home</TabsTrigger>
            <TabsTrigger value="Work" className="rounded-sm data-[state=active]:bg-orange-600 uppercase tracking-widest text-xs" data-testid="filter-work">Work</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
          <Input
            placeholder="Search equipment..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-zinc-900 border-zinc-800 focus:border-orange-500 rounded-sm"
            data-testid="equipment-search"
          />
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1,2,3].map(i => <div key={i} className="h-56 bg-zinc-900 animate-pulse rounded-sm" />)}
        </div>
      ) : items.length === 0 ? (
        <div className="border border-zinc-800 bg-zinc-900 rounded-sm p-12 text-center">
          <Tractor className="h-12 w-12 mx-auto text-zinc-700 mb-4" />
          <h3 className="font-display font-bold text-2xl mb-2">Empty garage</h3>
          <p className="text-zinc-400 mb-6">Pick from 15+ pre-loaded equipment types — we'll autofill maintenance intervals.</p>
          <Link to="/app/equipment/new">
            <Button className="bg-orange-600 hover:bg-orange-500 rounded-sm uppercase tracking-widest text-xs font-bold">
              <Plus className="w-4 h-4 mr-1" /> Add first equipment
            </Button>
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((eq) => (
            <Link
              key={eq.equipment_id}
              to={`/app/equipment/${eq.equipment_id}`}
              className="group bg-zinc-900 border border-zinc-800 hover:border-orange-600 hover:-translate-y-0.5 rounded-sm overflow-hidden transition-all duration-200"
              data-testid={`equipment-card-${eq.equipment_id}`}
            >
              <div className="aspect-[4/3] bg-zinc-950 relative overflow-hidden">
                {eq.photo_ids?.[0] ? (
                  <AuthImage fileId={eq.photo_ids[0]} className="w-full h-full object-cover" />
                ) : (
                  <img
                    src={PLACEHOLDERS[eq.category] || PLACEHOLDERS.Home}
                    alt={eq.name}
                    className="w-full h-full object-cover opacity-70 group-hover:opacity-90 transition-opacity"
                  />
                )}
                <div className="absolute top-2 left-2 tag-pill bg-zinc-950/90 text-orange-500 border border-orange-600/50">
                  {eq.category}
                </div>
                <div className="absolute top-2 right-2 tag-pill bg-zinc-950/90 text-zinc-300">
                  {eq.equipment_type}
                </div>
              </div>
              <div className="p-4">
                <h3 className="font-display font-bold text-xl tracking-tight truncate group-hover:text-orange-500 transition-colors">
                  {eq.name}
                </h3>
                <div className="text-xs text-zinc-500 mt-1 flex items-center gap-2">
                  {eq.year ? <span>{eq.year}</span> : null}
                  {eq.color ? <><span className="text-zinc-700">·</span><span className="capitalize">{eq.color}</span></> : null}
                  {eq.serial_number ? <><span className="text-zinc-700">·</span><span className="font-mono">SN {eq.serial_number.slice(0, 8)}</span></> : null}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
